// frontend/src/components/EditStockAdjustments.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  Package, Building, Warehouse, ArrowUp, ArrowDown, Calendar, FileText,
  Check, X, Clock, Loader2, AlertCircle, Save, DollarSign
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./StockAdjustments.css";

const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper to extract currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const EditStockAdjustments = () => {
  const { id } = useParams();
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency: contextFormatCurrency } = useCurrency();
  const navigate = useNavigate();

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  // ✅ Currency formatter
  const formatCurrency = useCallback((amount) => {
    const currencyCode = companyCurrency;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [companyCurrency]);

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adjustment, setAdjustment] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    warehouse_id: "",
    product_id: "",
    quantity: "",
    adjustment_type: "in",
    reason: "",
    adjustment_date: "",
    notes: "",
    status: "pending"
  });

  // Dropdown data
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productStock, setProductStock] = useState({});

  // Loading states
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [focusedFields, setFocusedFields] = useState({});

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const isCompanyAdmin = userType === 'company' && currentUser?.role === 'admin';
  const canEdit = isCompanyAdmin || isSystemAdmin;

  const getCompanyId = () => {
    if (adjustment) {
      return adjustment.company_id?._id || adjustment.company_id;
    }
    if (userType === 'company' && company) {
      return company._id || company;
    }
    return null;
  };

  // Fetch products for specific warehouse with stock info
  const fetchProductsForWarehouse = async (warehouseId) => {
    if (!warehouseId) {
      setFilteredProducts(products);
      setProductStock({});
      return;
    }

    try {
      setLoadingProducts(true);
      const companyId = getCompanyId();

      const response = await axios.get(`${url}/api/inventorystock/warehouse/${warehouseId}`, {
        params: {
          company_id: companyId,
          status: 'active'
        }
      });

      if (response.data.success) {
        const stockMap = {};
        const warehouseProducts = (response.data.data || []).map(stock => {
          const product = stock.product_id || {};
          stockMap[product._id] = {
            current_stock: stock.quantity || 0,
            available_stock: stock.available_qty || 0,
            avg_cost: stock.avg_cost || 0
          };
          return {
            _id: product._id,
            product_id: product._id,
            product_code: product.product_code || 'N/A',
            product_name: product.product_name || 'Unknown',
            current_stock: stock.quantity || 0,
            available_stock: stock.available_qty || 0,
            avg_cost: stock.avg_cost || 0
          };
        });

        setProductStock(stockMap);
        setFilteredProducts(warehouseProducts);
      } else {
        setFilteredProducts(products);
        setProductStock({});
      }
    } catch (error) {
      setFilteredProducts(products);
      setProductStock({});
    } finally {
      setLoadingProducts(false);
    }
  };

  // Handle warehouse change
  const handleWarehouseChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (value) {
      fetchProductsForWarehouse(value);
    } else {
      setFilteredProducts(products);
      setProductStock({});
    }
  };

  // Fetch adjustment data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch adjustment details
        const adjustmentRes = await axios.get(`${url}/api/stockadjustments/${id}`);

        const adjData = adjustmentRes.data.data;
        setAdjustment(adjData);

        // Get company ID for dropdowns
        const companyId = adjData.company_id?._id || adjData.company_id;

        // Fetch dropdown data
        await fetchDropdownData(companyId);

        // Set form data - quantity is absolute value for display
        setFormData({
          warehouse_id: adjData.warehouse_id?._id || adjData.warehouse_id || "",
          product_id: adjData.product_id?._id || adjData.product_id || "",
          quantity: Math.abs(adjData.quantity)?.toString() || "",
          adjustment_type: adjData.adjustment_type || "in",
          reason: adjData.reason || "",
          adjustment_date: adjData.adjustment_date
            ? new Date(adjData.adjustment_date).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          notes: adjData.notes || "",
          status: adjData.status || "pending"
        });

        // Fetch products for warehouse if warehouse is selected
        if (adjData.warehouse_id?._id || adjData.warehouse_id) {
          const warehouseId = adjData.warehouse_id?._id || adjData.warehouse_id;
          await fetchProductsForWarehouse(warehouseId);
        }

      } catch (error) {
        setError(error.message);

        let errorMsg = "Failed to load stock adjustment";
        if (error.response) {
          errorMsg = error.response.data?.message || errorMsg;
          if (error.response.status === 404) {
            errorMsg = "Stock adjustment not found";
          }
        }

        toast.error(errorMsg);
        navigate("/ViewStockAdjustments");
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && id) {
      fetchData();
    }
  }, [currentUser, id, navigate]);

  // Fetch dropdown data
  const fetchDropdownData = async (companyId) => {
    if (!companyId) return;

    try {
      const params = { company_id: companyId, status: 'active' };

      // Fetch warehouses
      try {
        const whRes = await axios.get(`${url}/api/warehouse/dropdown`, { params });
        if (whRes.data?.success) {
          setWarehouses(whRes.data.data || []);
        }
      } catch (err) {
        // Silent fail
      }

      // Fetch products
      try {
        const prodRes = await axios.get(`${url}/api/products/dropdown`, { params });
        if (prodRes.data?.success) {
          setProducts(prodRes.data.data || []);
          setFilteredProducts(prodRes.data.data || []);
        }
      } catch (err) {
        // Silent fail
      }

    } catch (error) {
      // Silent fail
    }
  };

  // Handlers
  const handleFocus = (field) =>
    setFocusedFields((prev) => ({ ...prev, [field]: true }));

  const handleBlur = (field) => {
    const value = formData[field];
    setFocusedFields((prev) => ({
      ...prev,
      [field]: (typeof value === 'string' ? value.trim() : value) ? true : false
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'warehouse_id') {
      handleWarehouseChange(e);
    } else if (name === 'quantity') {
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = () => {
    if (!formData.warehouse_id) {
      toast.error("Warehouse is required");
      return false;
    }
    if (!formData.product_id) {
      toast.error("Product is required");
      return false;
    }
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      toast.error("Quantity must be greater than 0");
      return false;
    }
    if (!formData.reason) {
      toast.error("Reason is required");
      return false;
    }

    // For out adjustments, check stock availability (only if quantity changed)
    if (formData.adjustment_type === 'out' && adjustment) {
      const originalQty = Math.abs(adjustment.quantity) || 0;
      const newQty = parseFloat(formData.quantity);
      const additionalQty = Math.max(0, newQty - originalQty);

      if (additionalQty > 0) {
        const stockInfo = productStock[formData.product_id];
        if (stockInfo && stockInfo.current_stock < additionalQty) {
          toast.error(`Insufficient stock. Available: ${stockInfo.current_stock}, Additional needed: ${additionalQty}`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Confirm if changing quantity that affects inventory
    if (adjustment && adjustment.status === 'approved') {
      toast.error("Cannot edit an approved adjustment");
      return;
    }

    const newQty = parseFloat(formData.quantity);
    const signedQuantity = formData.adjustment_type === 'in' ? newQty : -newQty;

    if (adjustment && adjustment.status === 'pending' && Math.abs(adjustment.quantity) !== newQty) {
      const confirm = window.confirm(
        `Changing quantity from ${Math.abs(adjustment.quantity)} to ${newQty} will affect the adjustment. Continue?`
      );
      if (!confirm) return;
    }

    setSubmitting(true);
    try {
      const payload = {
        warehouse_id: formData.warehouse_id,
        product_id: formData.product_id,
        quantity: signedQuantity,
        adjustment_type: formData.adjustment_type,
        reason: formData.reason,
        adjustment_date: formData.adjustment_date,
        notes: formData.notes || "",
        status: formData.status,
      };

      const res = await axios.put(`${url}/api/stockadjustments/${id}`, payload, {
        headers: {
          "Content-Type": "application/json"
        },
      });

      if (res.data.success) {
        toast.success("Stock adjustment updated successfully!");

        setTimeout(() => {
          navigate("/ViewStockAdjustments");
        }, 1500);
      }
    } catch (error) {
      let errorMsg = "Failed to update stock adjustment";

      if (error.response) {
        errorMsg = error.response.data?.message || errorMsg;
        if (error.response.data?.errors) {
          errorMsg = Object.values(error.response.data.errors).join(", ");
        }
      }

      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/ViewStockAdjustments");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="branch-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading stock adjustment details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !adjustment) {
    return (
      <div className="form-wrapper">
        <div className="branch-form-container">
          <div className="error-container">
            <AlertCircle size={48} color="#dc3545" />
            <h2>Adjustment Not Found</h2>
            <p>{error || "Stock adjustment not found"}</p>
            <button
              className="cancel-btn"
              onClick={handleCancel}
              style={{ marginTop: '20px' }}
            >
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="branch-form-container">
        <h2 className="form-title">
          <Package size={24} /> Edit Stock Adjustment
        </h2>

        {/* Adjustment Info Banner */}
        <div className="entity-info">
          <div className="info-item">
            <Building size={14} />
            <span>Company: {adjustment.company_id?.company_name} ({adjustment.company_id?.company_code})</span>
            <span style={{ marginLeft: '12px', fontSize: '0.8rem', color: '#666' }}>
              Currency: {companyCurrency}
            </span>
          </div>
          <div className="info-item">
            <span>Adjustment ID: <strong>#{adjustment.adjustment_id}</strong></span>
          </div>
          <div className="info-item">
            <span>Reference: <strong>{adjustment.reference_id}</strong></span>
          </div>
          <div className="info-item">
            {adjustment.status === 'pending' && <Clock size={14} color="#fa8c16" />}
            {adjustment.status === 'approved' && <Check size={14} color="#52c41a" />}
            {adjustment.status === 'rejected' && <X size={14} color="#ff4d4f" />}
            <span>Status: <strong className={`status-${adjustment.status}`}>
              {adjustment.status.charAt(0).toUpperCase() + adjustment.status.slice(1)}
            </strong></span>
          </div>
          <div className="info-item">
            <span>Current Type: <strong style={{ color: adjustment.adjustment_type === 'in' ? '#52c41a' : '#ff4d4f' }}>
              {adjustment.adjustment_type === 'in' ? 'Stock In (+)' : 'Stock Out (-)'}
            </strong></span>
          </div>
          <div className="info-item">
            <span>Current Quantity: <strong style={{ color: adjustment.adjustment_type === 'in' ? '#52c41a' : '#ff4d4f' }}>
              {adjustment.adjustment_type === 'in' ? '+' : '-'}{Math.abs(adjustment.quantity)}
            </strong></span>
          </div>
          <div className="info-item">
            <span>Created: {formatDate(adjustment.created_at)}</span>
          </div>
          {adjustment.approved_by && (
            <div className="info-item">
              <span>Approved by: {adjustment.approver?.Usercomp_sure_name} on {formatDate(adjustment.approved_at)}</span>
            </div>
          )}
          {adjustment.rejected_by && (
            <div className="info-item">
              <span>Rejected by: {adjustment.rejecter?.Usercomp_sure_name} on {formatDate(adjustment.rejected_at)}</span>
            </div>
          )}
        </div>

        <form className="branch-form" onSubmit={handleSubmit}>
          {/* Warehouse Selection */}
          <div className="form-field filled">
            <select
              name="warehouse_id"
              value={formData.warehouse_id}
              onChange={handleChange}
              required
              disabled={adjustment.status !== 'pending'}
            >
              <option value="">Select Warehouse *</option>
              {warehouses.map(warehouse => (
                <option key={warehouse._id} value={warehouse._id}>
                  {warehouse.warehouse_name} ({warehouse.warehouse_code})
                </option>
              ))}
            </select>
            <label>Warehouse *</label>
          </div>

          {/* Product Selection */}
          <div className="form-field filled">
            <select
              name="product_id"
              value={formData.product_id}
              onChange={handleChange}
              required
              disabled={!formData.warehouse_id || loadingProducts || adjustment.status !== 'pending'}
            >
              <option value="">
                {!formData.warehouse_id ? "Select warehouse first" :
                 loadingProducts ? "Loading products..." :
                 "Select Product *"}
              </option>
              {filteredProducts.map(product => (
                <option key={product._id} value={product._id}>
                  {product.product_name} ({product.product_code})
                  {productStock[product._id] && ` - Stock: ${productStock[product._id].current_stock}`}
                  {productStock[product._id] && productStock[product._id].avg_cost > 0 &&
                    ` @ ${formatCurrency(productStock[product._id].avg_cost)}`}
                </option>
              ))}
            </select>
            <label>Product *</label>
            {formData.product_id && productStock[formData.product_id] && (
              <small style={{ display: 'block', marginTop: 4, color: '#666' }}>
                Current Stock: {productStock[formData.product_id].current_stock} units
                {productStock[formData.product_id].avg_cost > 0 &&
                  ` | Avg Cost: ${formatCurrency(productStock[formData.product_id].avg_cost)}`}
              </small>
            )}
          </div>

          {/* Adjustment Type */}
          <div className="form-field">
            <select
              name="adjustment_type"
              value={formData.adjustment_type}
              onChange={handleChange}
              required
              disabled={adjustment.status !== 'pending'}
            >
              <option value="in">Stock In (+ Add to inventory)</option>
              <option value="out">Stock Out (- Remove from inventory)</option>
            </select>
            <label>Adjustment Type *</label>
          </div>

          {/* Quantity */}
          <div className={`form-field ${focusedFields.quantity || formData.quantity ? "filled" : ""}`}>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              onFocus={() => handleFocus("quantity")}
              onBlur={() => handleBlur("quantity")}
              placeholder="Adjustment quantity"
              min="0.01"
              step="0.01"
              required
              disabled={adjustment.status !== 'pending'}
            />
            <label>Quantity *</label>
          </div>

          {/* Adjustment Date */}
          <div className="form-field filled">
            <input
              type="date"
              name="adjustment_date"
              value={formData.adjustment_date}
              onChange={handleChange}
              required
              disabled={adjustment.status !== 'pending'}
            />
            <label>
              <Calendar size={14} className="inline-icon" /> Adjustment Date
            </label>
          </div>

          {/* Reason */}
          <div className={`form-field ${focusedFields.reason || formData.reason ? "filled" : ""}`}>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              onFocus={() => handleFocus("reason")}
              onBlur={() => handleBlur("reason")}
              placeholder="Enter reason for adjustment..."
              rows="3"
              required
              disabled={adjustment.status !== 'pending'}
            />
            <label>
              <FileText size={14} className="inline-icon" /> Reason *
            </label>
          </div>

          {/* Notes (Optional) */}
          <div className={`form-field ${focusedFields.notes || formData.notes ? "filled" : ""}`}>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              onFocus={() => handleFocus("notes")}
              onBlur={() => handleBlur("notes")}
              placeholder="Additional notes (optional)..."
              rows="2"
              disabled={adjustment.status !== 'pending'}
            />
            <label>
              <FileText size={14} className="inline-icon" /> Notes (Optional)
            </label>
          </div>

          {/* Status (only if not approved) */}
          {adjustment.status !== 'approved' && (
            <div className="form-field">
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <label>Status</label>
            </div>
          )}

          {/* Adjustment Preview */}
          <div className="adjustment-preview">
            <div className="preview-header">
              <Package size={16} />
              <span>Adjustment Summary</span>
            </div>
            <div className="preview-details">
              <div className="preview-item">
                <span>Type:</span>
                <span className={`preview-value ${formData.adjustment_type === 'in' ? 'in' : 'out'}`}>
                  {formData.adjustment_type === 'in' ? (
                    <>
                      <ArrowUp size={12} /> Stock In (+{formData.quantity || 0})
                    </>
                  ) : (
                    <>
                      <ArrowDown size={12} /> Stock Out (-{formData.quantity || 0})
                    </>
                  )}
                </span>
              </div>
              <div className="preview-item">
                <span>Status:</span>
                <span className={`preview-value status-${formData.status}`}>
                  {formData.status === 'pending' && <Clock size={12} />}
                  {formData.status === 'approved' && <Check size={12} />}
                  {formData.status === 'rejected' && <X size={12} />}
                  {formData.status.charAt(0).toUpperCase() + formData.status.slice(1)}
                </span>
              </div>
              {formData.product_id && productStock[formData.product_id] && (
                <div className="preview-item">
                  <span>Avg Cost:</span>
                  <span className="preview-value">
                    {formatCurrency(productStock[formData.product_id].avg_cost)}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="form-info">
            <small>
              <Package size={12} /> {adjustment.status === 'approved'
                ? "This adjustment has been approved and cannot be modified. Only status changes are allowed."
                : adjustment.status === 'rejected'
                ? "This adjustment has been rejected. You can edit and resubmit."
                : "Editing this adjustment will require re-approval."}
            </small>
          </div>

          <div className="button-group">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || adjustment.status === 'approved'}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Updating...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Update Adjustment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStockAdjustments;